import type React from 'react'
import { useState } from 'react'
import { useApiUrl, useNotification } from '@refinedev/core'
import { Typography, Button, Steps, Alert, Card, Space } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'

const { Title, Paragraph, Text } = Typography

// Scarica lo zip dell'estensione via fetch autenticato (header username/password
// da localStorage, stesso schema di fn.tsx fetchWithAuth) → blob → download.
// fetchWithAuth NON è riusabile qui perché fa response.text() (rompe il binario).
const downloadZip = async (apiUrl: string): Promise<void> => {
  const res = await fetch(`${apiUrl}/teddy-cart/download`, {
    headers: {
      username: localStorage.getItem('username') ?? '',
      password: localStorage.getItem('password') ?? ''
    }
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try { msg = (await res.json()).error ?? msg } catch { /* blob/non-json */ }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'teddy-cart.zip'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const ExtensionGuide: React.FC = () => {
  const apiUrl = useApiUrl()
  const { open } = useNotification()
  const [loading, setLoading] = useState(false)

  const onDownload = async (): Promise<void> => {
    setLoading(true)
    try {
      await downloadZip(apiUrl)
    } catch (e: any) {
      open?.({ type: 'error', message: 'Download fallito', description: e?.message ?? String(e) })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card style={{ maxWidth: 760, margin: '24px auto' }}>
      <Title level={3}>🧩 Estensione Teddy Cart</Title>
      <Paragraph>
        Estensione Chrome che cattura un carrello su <Text strong>ticketone.it</Text> o{' '}
        <Text strong>fansale.it</Text> e lo manda al buyer per completare l&apos;acquisto.
      </Paragraph>

      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Button type='primary' icon={<DownloadOutlined />} loading={loading} onClick={() => { void onDownload() }}>
          Scarica estensione (.zip)
        </Button>

        <Steps
          direction='vertical'
          size='small'
          current={-1}
          items={[
            { title: 'Scarica ed estrai', description: 'Scarica lo zip e estrailo in una cartella stabile (non rinominarla/spostarla dopo).' },
            { title: 'Apri le estensioni', description: 'Vai su chrome://extensions (copia/incolla nella barra indirizzi).' },
            { title: 'Modalità sviluppatore', description: 'Attiva il toggle "Modalità sviluppatore" in alto a destra.' },
            { title: 'Carica non pacchettizzata', description: 'Clicca "Carica estensione non pacchettizzata" e seleziona la cartella estratta (quella che contiene manifest.json).' },
            { title: 'Fissa l\'icona', description: 'Clicca l\'icona puzzle 🧩 nella toolbar e fissa "Teddy Cart → Buyer".' },
            { title: 'Uso', description: 'Su una pagina con un carrello ticketone/fansale, clicca l\'icona → (opzionale) spunta autopay → "Manda al buyer".' }
          ]}
        />

        <Alert
          type='info'
          showIcon
          message='Uso interno'
          description={'Lo zip include la configurazione (endpoint + chiave). Distribuiscilo solo internamente. Funziona su Chrome/Edge desktop. Se aggiorni l\'estensione, riscarica lo zip e premi "Aggiorna" su chrome://extensions.'}
        />
      </Space>
    </Card>
  )
}
